/**
 * Persona type definitions and icons for Starforge workers
 */

// Import persona icon images (used in sci-fi themes)
import architectIcon from '../assets/icons/sf_architect.png'
import cantorIcon from '../assets/icons/sf_cantor.png'
import chaosIcon from '../assets/icons/sf_chaos.png'
import demiurgeIcon from '../assets/icons/sf_demiurge.png'
import gatekeeperIcon from '../assets/icons/sf_gatekeeper.png'
import lurkerIcon from '../assets/icons/sf_lurker.png'
import nexusIcon from '../assets/icons/sf_nexus.png'
import prismIcon from '../assets/icons/sf_prism.png'
import sentinelIcon from '../assets/icons/sf_sentinel.png'
import terraformerIcon from '../assets/icons/sf_terraformer.png'
import visionaryIcon from '../assets/icons/sf_visionary.png'
import voyagerIcon from '../assets/icons/sf_voyager.png'
import weaverIcon from '../assets/icons/sf_weaver.png'

export const PERSONA_ICONS: Record<string, string> = {
  // Core personas
  nexus: nexusIcon,
  voyager: voyagerIcon,
  architect: architectIcon,
  prism: prismIcon,
  visionary: visionaryIcon,
  cantor: cantorIcon,
  sentinel: sentinelIcon,
  lurker: lurkerIcon,
  weaver: weaverIcon,
  chaos: chaosIcon,
  // Legacy/fallback
  demiurge: demiurgeIcon,
  gatekeeper: gatekeeperIcon,
  terraformer: terraformerIcon,
}

/** Display names for sci-fi themes (glass-morphism, soft-shadows) */
export const PERSONA_NAMES: Record<string, string> = {
  nexus: 'Orchestrator',
  voyager: 'Explorer',
  architect: 'Planner',
  prism: 'Splitter',
  visionary: 'Creative',
  cantor: 'Implementer',
  sentinel: 'Guardian',
  lurker: 'Observer',
  weaver: 'Integrator',
  chaos: 'Chaos',
  demiurge: 'Builder',
  gatekeeper: 'Reviewer',
  terraformer: 'Infrastructure',
}

/** Display names for corporate theme — professional role labels */
export const PERSONA_NAMES_CORPORATE: Record<string, string> = {
  nexus: 'Orchestrator',
  voyager: 'Explorer',
  architect: 'Planner',
  prism: 'Splitter',
  visionary: 'Frontend',
  cantor: 'Scribe',
  sentinel: 'Reviewer',
  lurker: 'Observer',
  weaver: 'Integrator',
  chaos: 'Red Team',
  demiurge: 'Builder',
  gatekeeper: 'Database',
  terraformer: 'Infrastructure',
}

/** Lucide icon name for each persona (used in corporate theme) */
export const PERSONA_LUCIDE_ICONS: Record<string, string> = {
  nexus: 'network',
  voyager: 'compass',
  architect: 'drafting-compass',
  prism: 'git-branch',
  visionary: 'layout',
  cantor: 'file-text',
  sentinel: 'shield-check',
  lurker: 'eye',
  weaver: 'git-merge',
  chaos: 'bug',
  demiurge: 'wrench',
  gatekeeper: 'database',
  terraformer: 'server',
}

/** Color pairs for corporate persona icons: bg = neutral circle, icon = medium-saturation distinct color */
export const PERSONA_CORPORATE_COLORS: Record<string, { bg: string; icon: string }> = {
  nexus: { bg: '#e2e8f0', icon: '#2563eb' },      // blue — orchestrator
  voyager: { bg: '#e2e8f0', icon: '#7c3aed' },     // violet — explorer
  architect: { bg: '#e2e8f0', icon: '#0891b2' },    // teal — planner
  prism: { bg: '#e2e8f0', icon: '#9333ea' },        // purple — splitter
  visionary: { bg: '#e2e8f0', icon: '#db2777' },    // pink — frontend
  cantor: { bg: '#e2e8f0', icon: '#b45309' },       // amber — scribe
  sentinel: { bg: '#e2e8f0', icon: '#059669' },     // emerald — reviewer
  lurker: { bg: '#e2e8f0', icon: '#475569' },       // slate — observer
  weaver: { bg: '#e2e8f0', icon: '#4338ca' },       // indigo — integrator
  chaos: { bg: '#e2e8f0', icon: '#dc2626' },        // red — red team
  demiurge: { bg: '#e2e8f0', icon: '#ea580c' },     // orange — builder
  gatekeeper: { bg: '#e2e8f0', icon: '#0369a1' },   // sky — database
  terraformer: { bg: '#e2e8f0', icon: '#65a30d' },  // lime — infrastructure
}

/** Glow colors for sci-fi persona icons in GraphView (used as box-shadow) */
export const PERSONA_GLOW_COLORS: Record<string, string> = {
  nexus: 'rgba(59, 130, 246, 0.6)',       // blue
  voyager: 'rgba(139, 92, 246, 0.6)',      // violet
  architect: 'rgba(6, 182, 212, 0.6)',     // cyan
  prism: 'rgba(168, 85, 247, 0.6)',        // purple
  visionary: 'rgba(236, 72, 153, 0.6)',    // pink
  cantor: 'rgba(245, 158, 11, 0.6)',       // amber
  sentinel: 'rgba(16, 185, 129, 0.6)',     // emerald
  lurker: 'rgba(100, 116, 139, 0.5)',      // slate
  weaver: 'rgba(99, 102, 241, 0.6)',       // indigo
  chaos: 'rgba(239, 68, 68, 0.6)',         // red
  demiurge: 'rgba(249, 115, 22, 0.6)',     // orange
  gatekeeper: 'rgba(14, 165, 233, 0.6)',   // sky
  terraformer: 'rgba(132, 204, 22, 0.6)',  // lime
}

export const DEFAULT_PERSONA_ICON = nexusIcon

export const PERSONA_TIERS: Record<string, number> = {
  nexus: 1,
  voyager: 1,
  lurker: 1,
  architect: 2,
  prism: 2,
  cantor: 2,
  sentinel: 2,
  chaos: 2,
  visionary: 3,
  demiurge: 3,
  terraformer: 3,
  gatekeeper: 3,
  weaver: 3,
}

export const TIER_PERSONAS: Record<number, string[]> = {
  1: ['nexus', 'voyager', 'lurker'],
  2: ['architect', 'prism', 'cantor', 'sentinel', 'chaos'],
  3: ['visionary', 'demiurge', 'terraformer', 'gatekeeper', 'weaver'],
}

export function getPersonaTier(persona: string): number {
  return PERSONA_TIERS[persona.toLowerCase()] ?? 3
}

/**
 * Get the PNG icon path for a persona (sci-fi themes), with fallback to default
 */
export function getPersonaIcon(persona: string | null | undefined): string {
  if (!persona) return DEFAULT_PERSONA_ICON
  return PERSONA_ICONS[persona.toLowerCase()] || DEFAULT_PERSONA_ICON
}

/**
 * Get the display name for a persona, theme-aware.
 * Corporate: role labels (Orchestrator, Frontend, Scribe, etc.)
 * Sci-fi: persona names (Nexus, Voyager, Sentinel, etc.)
 */
export function getPersonaDisplayName(persona: string | null | undefined, isCorporate: boolean): string {
  if (!persona) return isCorporate ? 'Orchestrator' : 'Nexus'
  const key = persona.toLowerCase()
  if (isCorporate) {
    return PERSONA_NAMES_CORPORATE[key] || persona
  }
  // Sci-fi themes: show the persona's proper name (capitalized key)
  return key.charAt(0).toUpperCase() + key.slice(1)
}
